import { Fragment, useMemo, useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Settings,
  Bell,
  ChevronLeft,
  ChevronRight,
  LogOut,
  Sun,
  Moon,
  Monitor,
  Menu,
  User,
  ChevronDown,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Avatar, AvatarFallback } from './ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { ScrollArea } from './ui/scroll-area';
import { Separator } from './ui/separator';
import {
  useAuthStore,
  useEntityStore,
  useThemeStore,
  useSidebarStore,
  useAlertsStore,
  PSEUDO_ENTITIES,
} from '../store';
import { authAPI, attentionAPI } from '../lib/api';
import { NAV_BY_SCOPE, SYSTEM_NAV } from '../lib/navConfig';
import AssistantPanel from './assistant/AssistantPanel';
import { applyScopeChange } from '../lib/scopeNavigation';
import { deriveJurisdictionGroups, countryDotColor, GROUP_DISPLAY_META } from '../lib/jurisdictionGroups';

// `section` groups the picker with a line between groups (Fan, 2026-10-24):
// Overall | Property Portfolio + BR OS | Entities + Personal.
const SCOPE_OPTIONS = [
  { key: 'overall', label: 'Overall', icon: '\uD83C\uDF10', section: 'group' },
  { key: 'portfolio', label: 'Property Portfolio', icon: '\uD83C\uDFE0', section: 'business' },
  { key: 'br_os', label: 'BR OS', icon: '\uD83C\uDFE2', section: 'business' },
  { key: 'entities', label: 'Entities', icon: '\uD83E\uDDFE', section: 'legal' },
  { key: 'personal', label: 'Personal', icon: '\uD83D\uDC64', section: 'legal' },
];

/** Group a flat nav item array by the `group` field, preserving order. */
function groupNavItems(items) {
  const groups = [];
  const map = new Map();
  for (const item of items) {
    if (!map.has(item.group)) {
      const entry = { title: item.group, items: [] };
      map.set(item.group, entry);
      groups.push(entry);
    }
    map.get(item.group).items.push(item);
  }
  return groups;
}

function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isCollapsed, toggleSidebar } = useSidebarStore();
  const { user, logout } = useAuthStore();
  const { selectedEntity, setSelectedEntity, selectedScope, setSelectedScope, entities } = useEntityStore();
  const { theme, setTheme } = useThemeStore();
  const { unreadCount } = useAlertsStore();

  // Attention badge count (lightweight poll on mount + every 5 min)
  const [attentionCount, setAttentionCount] = useState(0);
  useEffect(() => {
    let cancelled = false;
    const fetchBadge = async () => {
      try {
        const { data } = await attentionAPI.getBadge();
        if (!cancelled) setAttentionCount(data.critical + data.warning);
      } catch (_) { /* silent */ }
    };
    fetchBadge();
    const interval = setInterval(fetchBadge, 5 * 60 * 1000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  // Entities scope: exclude personal entities from the picker and aggregates.
  const businessEntities = useMemo(
    () => entities.filter((e) => e.group_role !== 'personal'),
    [entities]
  );
  const entityScopeGroups = useMemo(
    () => deriveJurisdictionGroups(businessEntities),
    [businessEntities]
  );

  // Personal scope: filter entities and groups to personal only.
  const personalEntities = useMemo(
    () => entities.filter((e) => e.group_role === 'personal'),
    [entities]
  );
  const personalGroups = useMemo(
    () => deriveJurisdictionGroups(personalEntities),
    [personalEntities]
  );

  const handleEntityChange = async (entityKey) => {
    try {
      if (!PSEUDO_ENTITIES.includes(entityKey)) {
        const ent = entities.find((e) => e.id === entityKey);
        if (ent) await authAPI.updateEntity(ent.type);
      }
      // Switching entity within a scope still lands on that scope's dashboard
      // (same canonical handler as the scope picker/logo), so the current page
      // doesn't persist showing stale data for the newly selected entity.
      applyScopeChange(selectedScope, { setSelectedScope, setSelectedEntity, navigate, entity: entityKey });
    } catch (error) {
      console.error('Failed to update entity:', error);
    }
  };

  const selectedEntityObj = !PSEUDO_ENTITIES.includes(selectedEntity)
    ? entities.find((e) => e.id === selectedEntity)
    : null;

  // Resolve display label and color for group pseudo-entities.
  // Check both GROUP_DISPLAY_META (known groups) and derived scope-aware groups
  // (includes the fallback "Other" group).
  const activeGroups = selectedScope === 'personal' ? personalGroups : entityScopeGroups;
  const selectedGroupMeta = selectedEntity?.startsWith('group:')
    ? (Object.values(GROUP_DISPLAY_META).find((m) => m.groupKey === selectedEntity)
       || activeGroups.find((g) => g.groupKey === selectedEntity))
    : null;
  const allLabel = selectedScope === 'personal' ? 'All Personal' : 'All Entities';
  const selectedLabel = selectedGroupMeta
    ? selectedGroupMeta.label
    : selectedEntityObj ? selectedEntityObj.name : allLabel;
  const selectedColor = selectedGroupMeta
    ? selectedGroupMeta.dotColor
    : selectedEntityObj ? (countryDotColor[selectedEntityObj.country] || 'bg-slate-500') : 'bg-slate-500';

  const scopeOption = SCOPE_OPTIONS.find((s) => s.key === selectedScope) || SCOPE_OPTIONS[0];

  const navItems = NAV_BY_SCOPE[selectedScope] || NAV_BY_SCOPE.entities;
  const navGroups = groupNavItems(navItems);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Logo/wordmark is a "way back" to the Overall group-wide dashboard,
  // mirroring what selecting Overall in the scope picker does.
  const goToOverall = () =>
    applyScopeChange('overall', { setSelectedScope, setSelectedEntity, navigate });

  const themeIcon = {
    light: Sun,
    dark: Moon,
    system: Monitor,
  };
  const ThemeIcon = themeIcon[theme];

  return (
    <motion.aside
      initial={false}
      animate={{ width: isCollapsed ? 72 : 280 }}
      className="h-screen border-r border-border bg-card flex flex-col"
      data-testid="sidebar"
    >
      {/* Header */}
      <div className="h-16 flex items-center justify-between px-4 border-b border-border">
        <AnimatePresence>
          {!isCollapsed && (
            <motion.button
              type="button"
              onClick={goToOverall}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex items-center gap-2"
              data-testid="logo-home"
              aria-label="Go to Overall dashboard"
            >
              <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                <span className="text-primary-foreground font-bold text-sm">FA</span>
              </div>
              <span className="font-semibold text-lg tracking-tight">Finance Agent</span>
            </motion.button>
          )}
        </AnimatePresence>
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleSidebar}
          className="h-8 w-8"
          data-testid="toggle-sidebar-btn"
        >
          {isCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
        </Button>
      </div>

      {/* Scope Selector */}
      <div className="p-3 pb-1.5">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              className={cn(
                'w-full justify-between gap-2 h-10',
                isCollapsed && 'px-2 justify-center'
              )}
              data-testid="scope-selector"
            >
              <div className="flex items-center gap-2 min-w-0">
                <span className="shrink-0">{scopeOption.icon}</span>
                {!isCollapsed && (
                  <span className="font-medium truncate">{scopeOption.label}</span>
                )}
              </div>
              {!isCollapsed && <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-64">
            <DropdownMenuLabel className="text-xs">Scope</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {SCOPE_OPTIONS.map((opt, idx) => (
              <Fragment key={opt.key}>
              {idx > 0 && SCOPE_OPTIONS[idx - 1].section !== opt.section && (
                <DropdownMenuSeparator data-testid={`scope-separator-${opt.key}`} />
              )}
              <DropdownMenuItem
                onClick={() =>
                  // Always navigate to the target scope's dashboard and reset
                  // the sub-selector to its aggregate (implemented once in
                  // applyScopeChange, so every scope-change path is identical).
                  applyScopeChange(opt.key, { setSelectedScope, setSelectedEntity, navigate })
                }
                className={cn(selectedScope === opt.key && 'bg-muted')}
              >
                <span className="mr-2">{opt.icon}</span>
                {opt.label}
              </DropdownMenuItem>
              </Fragment>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Entity Selector (entities and personal scopes) */}
      {(selectedScope === 'entities' || selectedScope === 'personal') && (
        <div className="px-3 pb-3 pt-1.5">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  'w-full justify-between gap-2 h-10',
                  isCollapsed && 'px-2 justify-center'
                )}
                data-testid="entity-selector"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <div className={cn('w-2 h-2 rounded-full shrink-0', selectedColor)} />
                  {!isCollapsed && (
                    <span className="font-medium truncate">{selectedLabel}</span>
                  )}
                </div>
                {!isCollapsed && <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-64 max-h-[70vh] overflow-y-auto">
              <DropdownMenuItem
                onClick={() => handleEntityChange('all')}
                className={cn(selectedEntity === 'all' && 'bg-muted')}
              >
                <div className={cn('w-2 h-2 rounded-full mr-2 bg-slate-500')} />
                {allLabel}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {(selectedScope === 'personal' ? personalGroups : entityScopeGroups).map((group) => (
                <div key={group.label}>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => handleEntityChange(group.groupKey)}
                    className={cn(
                      'text-xs font-medium text-muted-foreground flex items-center gap-1.5',
                      selectedEntity === group.groupKey && 'bg-muted text-foreground'
                    )}
                  >
                    <span>{group.flag}</span> {group.label}
                  </DropdownMenuItem>
                  {group.entities.map((ent) => (
                      <DropdownMenuItem
                        key={ent.id}
                        onClick={() => handleEntityChange(ent.id)}
                        className={cn(selectedEntity === ent.id && 'bg-muted', 'pl-6')}
                      >
                        <div className={cn('w-2 h-2 rounded-full mr-2 shrink-0', countryDotColor[ent.country] || 'bg-slate-500')} />
                        <span className="truncate">{ent.name}</span>
                      </DropdownMenuItem>
                  ))}
                </div>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      <Separator />

      {/* Navigation */}
      <ScrollArea className="flex-1 px-3 py-2">
        {navGroups.map((section) => (
          <div key={section.title} className="mb-4">
            {!isCollapsed && (
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-3 mb-2">
                {section.title}
              </p>
            )}
            <div className="space-y-1">
              {section.items.map((item) => {
                const isActive = item.matchPrefix
                  ? location.pathname.startsWith(item.matchPrefix)
                  : location.pathname === item.path;
                return (
                  <Link key={item.path} to={item.path}>
                    <Button
                      variant={isActive ? 'secondary' : 'ghost'}
                      className={cn(
                        'w-full justify-start gap-3 h-10',
                        isCollapsed && 'justify-center px-2',
                        isActive && 'bg-primary/10 text-primary hover:bg-primary/15'
                      )}
                      data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                    >
                      <item.icon className="h-4 w-4 flex-shrink-0" />
                      {!isCollapsed && <span>{item.label}</span>}
                    </Button>
                  </Link>
                );
              })}
            </div>
          </div>
        ))}

        {/* System nav (always visible) */}
        <div className="mb-4">
          {!isCollapsed && (
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-3 mb-2">
              System
            </p>
          )}
          <div className="space-y-1">
            {SYSTEM_NAV.map((item) => {
              const isActive = location.pathname === item.path;
              const isAlerts = item.path === '/alerts';
              const isAttention = item.path === '/attention';
              const badgeCount = isAlerts ? unreadCount : isAttention ? attentionCount : 0;
              return (
                <Link key={item.path} to={item.path}>
                  <Button
                    variant={isActive ? 'secondary' : 'ghost'}
                    className={cn(
                      'w-full justify-start gap-3 h-10',
                      isCollapsed && 'justify-center px-2',
                      isActive && 'bg-primary/10 text-primary hover:bg-primary/15'
                    )}
                    data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  >
                    {badgeCount > 0 ? (
                      <div className="relative">
                        <item.icon className="h-4 w-4 flex-shrink-0" />
                        <span className={cn(
                          "absolute -top-1 -right-1 w-3 h-3 rounded-full text-[8px] text-white flex items-center justify-center",
                          isAttention ? "bg-amber-500" : "bg-destructive"
                        )}>
                          {badgeCount > 9 ? '9+' : badgeCount}
                        </span>
                      </div>
                    ) : (
                      <item.icon className="h-4 w-4 flex-shrink-0" />
                    )}
                    {!isCollapsed && <span>{item.label}</span>}
                    {!isCollapsed && badgeCount > 0 && (
                      <Badge
                        variant={isAttention ? "outline" : "destructive"}
                        className={cn("ml-auto", isAttention && "border-amber-500 text-amber-700 dark:text-amber-400")}
                      >
                        {badgeCount}
                      </Badge>
                    )}
                  </Button>
                </Link>
              );
            })}
          </div>
        </div>
      </ScrollArea>

      <Separator />

      {/* Bottom Actions */}
      <div className="p-3 space-y-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className={cn('w-full justify-start gap-3 h-10', isCollapsed && 'justify-center px-2')}
            >
              <ThemeIcon className="h-4 w-4" />
              {!isCollapsed && <span>Theme</span>}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => setTheme('light')}>
              <Sun className="h-4 w-4 mr-2" /> Light
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setTheme('dark')}>
              <Moon className="h-4 w-4 mr-2" /> Dark
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setTheme('system')}>
              <Monitor className="h-4 w-4 mr-2" /> System
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <Separator />

      {/* User */}
      <div className="p-3">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className={cn('w-full justify-start gap-3 h-12', isCollapsed && 'justify-center px-2')}
              data-testid="user-menu"
            >
              <Avatar className="h-8 w-8">
                <AvatarFallback className="bg-primary text-primary-foreground text-sm">
                  {user?.name?.charAt(0) || 'U'}
                </AvatarFallback>
              </Avatar>
              {!isCollapsed && (
                <div className="flex flex-col items-start">
                  <span className="text-sm font-medium truncate max-w-[140px]">{user?.name}</span>
                  <span className="text-xs text-muted-foreground truncate max-w-[140px]">{user?.email}</span>
                </div>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>My Account</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate('/settings')}>
              <User className="h-4 w-4 mr-2" /> Profile
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate('/settings')}>
              <Settings className="h-4 w-4 mr-2" /> Settings
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout} className="text-destructive">
              <LogOut className="h-4 w-4 mr-2" /> Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </motion.aside>
  );
}

function TopBar() {
  const { user } = useAuthStore();
  const { unreadCount } = useAlertsStore();
  const { toggleSidebar } = useSidebarStore();
  const navigate = useNavigate();

  return (
    <header className="h-16 border-b border-border bg-card/50 backdrop-blur-sm flex items-center justify-between px-6 md:hidden">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={toggleSidebar}>
          <Menu className="h-5 w-5" />
        </Button>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-primary flex items-center justify-center">
            <span className="text-primary-foreground font-bold text-xs">FA</span>
          </div>
          <span className="font-semibold">Finance Agent</span>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="icon" onClick={() => navigate('/alerts')}>
          <div className="relative">
            <Bell className="h-5 w-5" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-destructive rounded-full text-[10px] text-white flex items-center justify-center">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </div>
        </Button>
        <Avatar className="h-8 w-8">
          <AvatarFallback className="bg-primary text-primary-foreground text-sm">
            {user?.name?.charAt(0) || 'U'}
          </AvatarFallback>
        </Avatar>
      </div>
    </header>
  );
}

export function DashboardLayout({ children }) {
  const { isCollapsed } = useSidebarStore();

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      <div className="hidden md:block">
        <Sidebar />
      </div>
      <div className="flex-1 flex flex-col overflow-hidden">
        <TopBar />
        <main className="flex-1 overflow-auto">
          <div className="p-6 md:p-8 lg:p-10 max-w-[1600px] mx-auto w-full">
            {children}
          </div>
        </main>
      </div>
      <AssistantPanel />
    </div>
  );
}
